import { SNSC } from './native-token';

export interface UpdateLogEntry {
  /** Stable release ID. A new ID also announces the release on the homepage. */
  id: string;
  /** Calendar date in UTC, newest releases first. */
  date: string;
  title: string;
  summary: string;
  highlights: readonly { title: string; description: string }[];
  link?: { label: string; to: string };
}

/** Add each new app update at the top; the timeline and popup share this source. */
export const UPDATE_LOGS: readonly UpdateLogEntry[] = [
  {
    id: '2026-11-03-daily-reactions-trending',
    date: '2026-11-03',
    title: 'Your daily take on Soon',
    summary: 'Trending follows community reactions, with one paid reaction per wallet and token each UTC day.',
    highlights: [
      { title: 'Trending by reactions', description: 'Tokens rank by their total confirmed emoji reactions across all four emoji types, with counts visible beside each token.' },
      { title: 'One token, one daily reaction', description: 'React to your own token or any other token once per UTC day from each paying wallet. You can react to other tokens that day, and your allowance resets at midnight UTC. Each reaction still costs 1 SNSC plus network fees.' },
    ],
    link: { label: 'Explore Trending', to: '/trending' },
  },
  {
    id: '2026-11-03-compact-status-bar',
    date: '2026-11-03',
    title: 'More room for the market',
    summary: 'Platform stats now sit in a slim black status bar above every page.',
    highlights: [
      { title: 'Stats on one line', description: 'Labels and values sit side by side in a 30-pixel strip. Scroll across the figures and open Stats details for their definitions and coverage.' },
    ],
    link: { label: 'See the slimmer status bar', to: '/' },
  },
  {
    id: '2026-11-03-platform-analytics',
    date: '2026-11-03',
    title: 'Soonscreener, by the numbers',
    summary: 'A white-on-black stats strip puts site activity, SNSC and app resources above every page.',
    highlights: [
      { title: 'Activity in view', description: 'Follow daily active visitors, visitors online, site visits, unique browser visitors, signed accounts and screened tokens. Usage counts start with this release and include their measurement windows.' },
      { title: 'SNSC and ICP at a glance', description: 'See SNSC’s verified price and circulating market cap alongside cached ICP totals, with app balances and observed user-controlled wallet balances shown separately.' },
      { title: 'Measured resource usage', description: 'Track Soon-reported burn from Soonscreener-owned mining rigs. Website resource counters show their source, coverage and last sample; missing or delayed data stays clearly labelled.' },
    ],
    link: { label: 'See the platform stats', to: '/' },
  },
  {
    id: '2026-11-03-update-logs',
    date: '2026-11-03',
    title: 'A home for every update',
    summary: 'See what has changed on Soonscreener and catch the latest features when you visit.',
    highlights: [
      { title: 'The update timeline', description: 'Browse product updates in one place, with the most recent changes at the top and links to try the features.' },
      { title: 'Latest features first', description: 'A homepage popup introduces each new update. Dismiss it once on this browser, or open it again with “What’s new”.' },
    ],
    link: { label: 'Suggest what comes next', to: '/feature-requests' },
  },
  {
    id: '2026-11-03-holder-bubble-map',
    date: '2026-11-03',
    title: 'See the connections between holders',
    summary: 'The new Bubble map tab brings token holdings and recorded transfers into one interactive view.',
    highlights: [
      { title: 'Holdings, mapped', description: 'Bubble area represents each holder’s balance. Transfer links and coloured groups help you explore connections without treating them as proof of shared ownership.' },
      { title: 'Follow the evidence', description: 'Search for a wallet, select a holder, and inspect exact transferred amounts and ledger block ranges. Filter holders and transfer sizes, then drag, zoom or expand the map.' },
      { title: 'Shared snapshots', description: 'Maps use cached holder data, with snapshot age, indexing progress and relationship coverage shown beside the map.' },
    ],
    link: { label: 'Explore the SNSC bubble map', to: `${SNSC.tokenPath}/bubbles` },
  },
  {
    id: '2026-11-02-native-snsc-wallet',
    date: '2026-11-02',
    title: 'SNSC belongs in your wallet',
    summary: 'Soonscreener’s native token is now built into the account wallet alongside ICP.',
    highlights: [
      { title: 'Native SNSC balances', description: 'See SNSC by default, receive it at your account principal, and switch independently between your SNSC and ICP balances.' },
      { title: 'Reviewed token sends', description: 'Review the recipient, exact amount and live network fee before sending. Saved transfer requests support recovery when a reply is uncertain.' },
      { title: 'Ready for reactions', description: 'Token reactions link directly to your wallet so you can fund your account before choosing a reaction.' },
    ],
    link: { label: 'Open your wallet', to: '/account' },
  },
  {
    id: '2026-11-01-community-and-tools',
    date: '2026-11-01',
    title: 'More ways to take part in Soon',
    summary: 'Community profiles, trader tools and SNSC reactions give token communities more ways to participate.',
    highlights: [
      { title: 'Make a token profile your own', description: 'Paid profile updates support banner uploads, descriptions, social links, About sections and roadmaps. Recently updated profiles take the homepage spotlight.' },
      { title: 'Follow the market', description: 'Follow traders and receive in-app alerts. Eligible SNSC holders can also use advanced alerts and saved screeners.' },
      { title: 'Have your say', description: 'Signed-in users can submit and upvote feature requests. Paid token reactions add another way to respond to a project.' },
    ],
    link: { label: 'Browse feature requests', to: '/feature-requests' },
  },
];

export const latestUpdate = UPDATE_LOGS[0]!;
